import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { PageResponse } from '../interfaces/commercial.interface';
import {
    CommercialPartyType,
    ICommercialParty,
    ICreateCommercialPartyRequest,
    IUpdateCommercialPartyRequest,
} from '../interfaces/commercialParty.interface';

export interface CommercialPartyListParams {
    page?: number;
    size?: number;
    type?: CommercialPartyType;
    activeOnly?: boolean;
    q?: string;
}

/** Commercial party master CRUD (Task 4a, spec §2, 7.4). Returns data only -- components own
 * triggerDownload/showApiError per house rules (mirrors exportLc.service). No remove/delete
 * method -- referenced master data gets activate/deactivate, never delete. */
export const CommercialPartyService = {
    list: async (institutionId: string, params: CommercialPartyListParams = {}): Promise<PageResponse<ICommercialParty>> => {
        const { page = 0, size = 20, type, activeOnly, q } = params;
        const query = new URLSearchParams({ page: String(page), size: String(size) });
        if (type) query.set('type', type);
        if (activeOnly !== undefined) query.set('activeOnly', String(activeOnly));
        if (q) query.set('q', q);
        return (await axios.get(`${ENDPOINTS.COMMERCIAL_PARTY.LIST(institutionId)}?${query.toString()}`)).data;
    },
    getById: async (institutionId: string, id: string): Promise<ICommercialParty> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_PARTY.GET_BY_ID(institutionId, id))).data,
    /** Fix round 1: fetches a single party by id -- used by `CommercialPartyAutocomplete` to
     * hydrate a picker's pre-selection from a bare `*PartyId` when the party isn't already among
     * the loaded options (e.g. an inactive party, or one outside the picker's `partyTypes` filter
     * at edit time). Same GET as `getById`, named for what the picker calls it for. */
    getCommercialParty: async (institutionId: string, id: string): Promise<ICommercialParty> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_PARTY.GET_BY_ID(institutionId, id))).data,
    create: async (institutionId: string, dto: ICreateCommercialPartyRequest): Promise<ICommercialParty> =>
        (await axios.post(ENDPOINTS.COMMERCIAL_PARTY.CREATE(institutionId), dto)).data,
    update: async (institutionId: string, id: string, dto: IUpdateCommercialPartyRequest): Promise<ICommercialParty> =>
        (await axios.put(ENDPOINTS.COMMERCIAL_PARTY.UPDATE(institutionId, id), dto)).data,
    activate: async (institutionId: string, id: string): Promise<ICommercialParty> =>
        (await axios.post(ENDPOINTS.COMMERCIAL_PARTY.ACTIVATE(institutionId, id))).data,
    deactivate: async (institutionId: string, id: string): Promise<ICommercialParty> =>
        (await axios.post(ENDPOINTS.COMMERCIAL_PARTY.DEACTIVATE(institutionId, id))).data,
};
